import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { MenuItem } from "primereact/menuitem";
import { Toast } from "primereact/toast";
import { CSSProperties, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import { getErrorTitle } from "../../../constants/constants";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { combineLosAngelesDateAndTime, getLosAngelesDateKey, getLosAngelesTimeKey } from "../../../utils/losAngelesDateTime";

type MovementType = "in" | "out";

type ResidentEntryTrackerButtonProps = {
  residentId: string;
  residentName?: string;
  disabled?: boolean;
  size?: "small" | "large";
  style?: CSSProperties;
  createEntry: (residentId: string, payload: { movementType: MovementType; occurredAt: string; notes: string }) => Promise<any>;
  dashboardPath?: string;
  residentEntryStatus?: string;
};

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? 0 : 30;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const displayHour = hour % 12 || 12;
  return { value, label: `${displayHour}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}` };
});

const getNearestHalfHour = () => {
  const [hour, minute] = getLosAngelesTimeKey().split(":").map(Number);
  const totalMinutes = (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
  const rounded = Math.min(23 * 60 + 30, Math.floor(totalMinutes / 30) * 30);
  return `${String(Math.floor(rounded / 60)).padStart(2, "0")}:${String(rounded % 60).padStart(2, "0")}`;
};

const ResidentEntryTrackerButton = ({
  residentId,
  residentName = "Resident",
  disabled = false,
  size = "small",
  style,
  createEntry,
  dashboardPath,
  residentEntryStatus,
}: ResidentEntryTrackerButtonProps) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [movementType, setMovementType] = useState<MovementType | null>(null);
  const [timeKey, setTimeKey] = useState(getNearestHalfHour);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [currentEntryStatus, setCurrentEntryStatus] = useState(String(residentEntryStatus || "in").toLowerCase());
  const currentlyOut = currentEntryStatus === "out";
  const dateKey = getLosAngelesDateKey();
  const movementLabel = movementType === "in" ? "In" : "Out";
  const availableTimeOptions = useMemo(() => {
    const [hour, minute] = getLosAngelesTimeKey().split(":").map(Number);
    const currentMinutes = (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
    return TIME_OPTIONS.filter((option) => {
      const [optionHour, optionMinute] = option.value.split(":").map(Number);
      return optionHour * 60 + optionMinute <= currentMinutes;
    });
  }, [dateKey]);

  const menuItems = useMemo<MenuItem[]>(() => [
    { label: "In", icon: "pi pi-arrow-right", command: () => setMovementType("in") },
    { label: "Out", icon: "pi pi-arrow-left", command: () => setMovementType("out") },
    ...(dashboardPath ? [{ label: "View Dashboard", icon: "pi pi-chart-bar", command: () => navigate(dashboardPath) }] : []),
  ], [dashboardPath, navigate]);

  const close = () => {
    if (!saving) {
      setMovementType(null);
      setNotes("");
      setTimeKey(getNearestHalfHour());
    }
  };

  const save = async () => {
    if (!movementType || !residentId) {
      return;
    }
    setSaving(true);
    try {
      const createdEntry = await createEntry(residentId, {
        movementType,
        occurredAt: combineLosAngelesDateAndTime(dateKey, timeKey),
        notes: notes.trim(),
      });
      setCurrentEntryStatus(String(createdEntry?.residentEntryStatus || movementType).toLowerCase());
      toast.current?.show({ severity: "success", summary: "Entry recorded", detail: `${residentName} marked ${movementLabel} at ${TIME_OPTIONS.find((item) => item.value === timeKey)?.label || timeKey}.`, life: 3000 });
      setMovementType(null);
      setNotes("");
      setTimeKey(getNearestHalfHour());
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to record this entry.", life: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <ActionMenuButton
        label="Entry Tracker"
        icon="pi pi-sign-in"
        size={size}
        rounded
        disabled={disabled || !residentId}
        model={menuItems}
        style={{
          ...(style || {}),
          marginLeft: "0.5rem",
          color: currentlyOut ? "#991b1b" : "#166534",
          background: currentlyOut ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${currentlyOut ? "#fca5a5" : "#86efac"}`,
        }}
      />
      <Dialog
        visible={Boolean(movementType)}
        modal
        header={renderSurfaceHeader(`Record ${movementLabel}`, residentName, movementType === "in" ? "pi pi-arrow-right" : "pi pi-arrow-left")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={close}
      >
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={appMutedTextStyle}>Date: {dateKey} (America/Los_Angeles)</div>
          <div>
            <label htmlFor={`resident-entry-time-${residentId}`} className="block mb-2 font-medium">Exact time</label>
            <Dropdown
              inputId={`resident-entry-time-${residentId}`}
              value={timeKey}
              options={availableTimeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => setTimeKey(String(event.value || ""))}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor={`resident-entry-note-${residentId}`} className="block mb-2 font-medium">Note (optional)</label>
            <InputTextarea
              id={`resident-entry-note-${residentId}`}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              autoResize
              maxLength={1000}
              className="w-full"
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" severity="secondary" text rounded disabled={saving} onClick={close} />
            <Button type="button" label={`Record ${movementLabel}`} icon="pi pi-check" severity="warning" rounded loading={saving} onClick={save} />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ResidentEntryTrackerButton;
